<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
	message: { type: Object, required: true }
});

const expanded = ref(false);

const content = computed(() => props.message.content ?? {});
const toolName = computed(() => content.value.name ?? 'Unknown tool');

const isOptionsTool = computed(() => toolName.value === 'displayMultipleOptions');
const options = computed(() => content.value.arguments?.options ?? []);
</script>

<template>
  <div class="tool-call" :class="{ compact: isOptionsTool }">
    <template v-if="isOptionsTool">
      <div class="options-header">
        <span class="label">Options displayed</span>
      </div>

      <div class="options-list">
        <span
          v-for="option in options"
          :key="option"
          class="option-chip"
        >
          {{ option }}
        </span>
      </div>
    </template>

    <template v-else>
      <button class="tool-header" @click="expanded = !expanded">
        <div class="left">
          <span class="status-dot" />
          <span class="label">Tool call</span>
          <code>{{ toolName }}</code>
        </div>

        <span class="chevron" :class="{ open: expanded }">⌄</span>
      </button>

      <Transition name="expand">
        <pre v-if="expanded" class="payload">{{ JSON.stringify(content, null, 2) }}</pre>
      </Transition>
    </template>
  </div>
</template>


<style scoped lang="scss">
.tool-call {
  max-width: 680px;
  margin: 8px 0;
  border: 1px solid #d8dee8;
  border-radius: 12px;
  background: #f8fafc;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  font-size: 13px;
}

.tool-header {
  width: 100%;
  padding: 10px 12px;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  color: #334155;

  &:hover {
    background: #eef3f8;
  }
}

.left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.18);
}

.label {
  color: #64748b;
  font-weight: 500;
}

code {
  padding: 3px 7px;
  border-radius: 6px;
  background: #e2e8f0;
  color: #0f172a;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.chevron {
  transition: transform 0.2s ease;

  &.open {
    transform: rotate(180deg);
  }
}

.payload {
  margin: 0;
  padding: 12px;
  max-height: 420px;
  overflow: auto;
  background: #0f172a;
  color: #e2e8f0;
  font-size: 12px;
  line-height: 1.5;
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.18s ease;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.tool-call.compact {
  padding: 10px 12px;
}

.options-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.options-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.option-chip {
  padding: 5px 9px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #334155;
  font-size: 12px;
  font-weight: 500;
}
</style>
